/**
 * TEMPORARY GEO PROBE — measurement only, remove after evaluation.
 *
 * Measures whether IP geolocation can distinguish the Balearic islands
 * (Mallorca / Menorca / Ibiza) on real traffic. Runs once per session.
 *
 * This file must never mutate the DOM or surface anything to the visitor.
 * All failure paths are silent by design: a broken probe must not become a
 * broken storefront.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'mc_geo_probe';
  var LOOKUP_URL = 'https://ipwho.is/';
  var COLLECTOR_URL = 'https://miele-geo-probe.noel-bb3.workers.dev/probe';
  var TIMEOUT_MS = 3000;

  // Island names are matched only inside the Balearic autonomous community.
  // Without this gate the city patterns collide with real places elsewhere —
  // "Las Palmas de Gran Canaria" and "Palma Campania" both contain "palma",
  // "Sant Antoni de Vilamajor" is in Catalonia, "Vinca" contains "inca".
  var BALEARIC_REGION = /balear/i;

  // Only Menorca and Ibiza/Formentera need listing. Inside the region gate
  // every remaining municipality is on Mallorca (53 of them), so a catch-all
  // beats an incomplete Mallorca list that would drop places like
  // "Vilafranca de Bonany".
  var MENORCA_TERMS = [
    'menorca', 'mao', 'mahon', 'ciutadella', 'alaior', 'es castell',
    'sant lluis', 'ferreries', 'es mercadal', 'es migjorn gran', 'fornells'
  ];

  var IBIZA_TERMS = [
    'eivissa', 'ibiza', 'sant antoni', 'santa eularia', 'santa eulalia',
    'sant josep', 'sant joan de labritja', 'formentera', 'sant francesc',
    'es pujols', 'la savina'
  ];

  // Lowercase, strip diacritics, collapse whitespace, so "Maó-Mahón" and
  // "Santa Eulària des Riu" match their plain-ASCII list entries.
  function normalizeCity(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function containsAny(city, terms) {
    for (var i = 0; i < terms.length; i++) {
      if (city.indexOf(terms[i]) !== -1) return true;
    }
    return false;
  }

  function islandLabel(city, region, countryCode) {
    if (BALEARIC_REGION.test(region)) {
      var normalized = normalizeCity(city);
      // An absent city is not "some other Balearic municipality": defaulting
      // it to Mallorca would fabricate the very signal being measured.
      if (normalized) {
        if (containsAny(normalized, MENORCA_TERMS)) return 'menorca';
        if (containsAny(normalized, IBIZA_TERMS)) return 'ibiza';
        return 'mallorca';
      }
    }
    return countryCode === 'ES' ? 'unknown_es' : 'foreign';
  }

  /**
   * Shopify injects the Customer Privacy API at runtime when a consent banner
   * is configured, so its presence cannot be determined from theme source.
   * Feature-detect instead: honour consent when the API is there, run
   * unimpeded when it is not.
   */
  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') {
        return true;
      }
      return privacy.analyticsProcessingAllowed() === true;
    } catch (error) {
      return false; // API present but unhealthy: do not measure
    }
  }

  function report(payload) {
    try {
      fetch(COLLECTOR_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        mode: 'cors',
        credentials: 'omit'
      }).catch(function () {});
    } catch (error) {
      /* silent */
    }
  }

  function probe() {
    if (!analyticsAllowed()) return;

    try {
      if (sessionStorage.getItem(STORAGE_KEY) !== null) return;
    } catch (error) {
      return; // storage unavailable: cannot honour once-per-session, so skip
    }

    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, TIMEOUT_MS);

    fetch(LOOKUP_URL, {
      signal: controller.signal,
      mode: 'cors',
      credentials: 'omit'
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        // A lookup that reports failure is treated as a failure, not as a
        // data point: recording it would add phantom "foreign" rows.
        if (!data || data.success === false) return;

        var city = data.city || '';
        var region = data.region || '';
        var isp = (data.connection && data.connection.isp) || '';
        var island = islandLabel(city, region, data.country_code || '');

        console.log(
          '[geo-probe] city=' + city + ' region=' + region + ' island=' + island + ' isp=' + isp
        );

        report({ island: island, city: city, region: region, isp: isp });

        try {
          sessionStorage.setItem(STORAGE_KEY, island);
        } catch (error) {
          /* silent */
        }
      })
      .catch(function () {})
      .then(function () {
        clearTimeout(timer);
      });
  }

  function start() {
    try {
      probe();
    } catch (error) {
      /* silent */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
